import { format, subWeeks, subMonths, subYears, startOfDay, endOfDay, parse, isValid } from 'date-fns';
import type { DateRange } from 'react-day-picker';

export interface PresetRange {
  id: string;
  label: string;
  getValue: () => DateRange;
}

export const DATE_FORMAT = 'yyyy-MM-dd';

export const formatDateForApi = (date: Date): string => {
  return format(date, DATE_FORMAT);
};

export const createCustomPreset = (
  fromDate: string,
  toDate: string,
  t: (key: string) => string
): PresetRange | null => {
  if (!fromDate || !toDate) return null;

  const from = parse(fromDate, DATE_FORMAT, new Date());
  const to = parse(toDate, DATE_FORMAT, new Date());

  if (!isValid(from) || !isValid(to)) return null;

  return {
    id: 'CR',
    label: t('welcome.customrange.label'),
    getValue: () => ({ from, to })
  };
};

export const getPresetRanges = (t: (key: string) => string): PresetRange[] => {
  const today = endOfDay(new Date());

  return [
    {
      id: 'LW',
      label: t('welcome.lastweek.label'),
      getValue: () => ({
        from: startOfDay(subWeeks(today, 1)),
        to: today
      })
    },
    {
      id: 'L2W',
      label: t('welcome.last2weeks.label'),
      getValue: () => ({
        from: startOfDay(subWeeks(today, 2)),
        to: today
      })
    },
    {
      id: 'LM',
      label: t('welcome.lastmonth.label'),
      getValue: () => ({
        from: startOfDay(subMonths(today, 1)),
        to: today
      })
    },
    {
      id: 'L2M',
      label: t('welcome.last2month.label'),
      getValue: () => ({
        from: startOfDay(subMonths(today, 2)),
        to: today
      })
    },
    {
      id: 'L6M',
      label: t('welcome.last6month.label'),
      getValue: () => ({
        from: startOfDay(subMonths(today, 6)),
        to: today
      })
    },
    {
      id: 'LY',
      label: t('welcome.lastyear.label'),
      getValue: () => ({
        from: startOfDay(subYears(today, 1)),
        to: today
      })
    },
    {
      id: 'FE',
      label: t('welcome.forever.label'),
      getValue: () => ({
        from: startOfDay(subYears(today, 10)),
        to: today
      })
    }
  ];
};

export const checkIfPresetRange = (presets: PresetRange[], range?: DateRange): string | null => {
  if (!range?.from || !range?.to) return null;

  const rangeFromTime = startOfDay(range.from).getTime();
  const rangeToTime = endOfDay(range.to).getTime();

  for (const preset of presets) {
    const presetRange = preset.getValue();
    if (!presetRange.from || !presetRange.to) continue;

    const presetFromTime = startOfDay(presetRange.from).getTime();
    const presetToTime = endOfDay(presetRange.to).getTime();

    if (rangeFromTime === presetFromTime && rangeToTime === presetToTime) {
      return preset.id;
    }
  }

  return null;
};
